import { DOCUMENT, inject, Injectable, Renderer2 } from '@angular/core';
import { ApiServiceBase } from '@api/abstracts';
import { BehaviorSubject, map, Observable, ReplaySubject } from 'rxjs';
import { Instagram } from './model';

@Injectable()
export class InstagramService extends ApiServiceBase<Instagram> {
  readonly #isOpenDialog$ = new BehaviorSubject<boolean>(false);
  readonly #renderer = inject(Renderer2);
  readonly #selectedItemIndex$ = new ReplaySubject<number | null>();
  readonly #document = inject(DOCUMENT);
  readonly #window = this.#document.defaultView as Window;

  constructor() {
    super('instagram/medias/perene_officiel', 'instagram', 'fournierWebservice');
  }

  getInstagramWithCdnLinks(): Observable<Instagram> {
    return this.get('10', {}, { cacheExpirationTime: 600 }).pipe(
      map((instagram) => this.#replaceLinksWithCdn(instagram))
    );
  }

  isDialogOpened$(): BehaviorSubject<boolean> {
    return this.#isOpenDialog$;
  }

  getSelectedItem$(): ReplaySubject<number | null> {
    return this.#selectedItemIndex$;
  }

  setSelectedItemIndex(index: number): void {
    this.#selectedItemIndex$.next(index);
  }

  openDialog(itemIndex: number): void {
    this.#selectedItemIndex$.next(itemIndex);
    this.#renderer.addClass(this.#document.body, 'disabled-scrolling');
    const scrollbarSize = this.#window.innerWidth - this.#document.documentElement.clientWidth;
    if (scrollbarSize > 0) {
      const body = this.#document.body;
      const nav = this.#document.querySelector('.nav') as HTMLElement;
      nav?.style.setProperty('--replace-scrollbar-width', `${scrollbarSize / 10}rem`);
      body.style.setProperty('--replace-scrollbar-width', `${scrollbarSize / 10}rem`);
      nav?.classList.add('replace-scrollbar');
      this.#renderer.addClass(this.#document.body, 'replace-scrollbar');
    }
    this.#isOpenDialog$.next(true);
  }

  closeDialog(): void {
    this.#selectedItemIndex$.next(null);
    this.#renderer.removeClass(this.#document.body, 'replace-scrollbar');
    this.#renderer.removeClass(this.#document.body, 'disabled-scrolling');
    this.#document.querySelector('.nav')?.classList.remove('replace-scrollbar');
    this.#isOpenDialog$.next(false);
  }

  #replaceLinksWithCdn(instagram: Instagram): Instagram {
    const updatedAccount = {
      ...instagram.account,
      picture: {
        ...instagram.account.picture,
        link: instagram.account.picture.linkCdn ?? instagram.account.picture.link,
      },
    };

    const updatedItems = instagram.items.map((item) => ({
      ...item,
      images: item.images.map((image) => ({
        ...image,
        link: image.linkCdn ?? image.link,
      })),
    }));

    return {
      ...instagram,
      account: updatedAccount,
      items: updatedItems,
    };
  }
}
